@let cfg = statusConfig();

<div
  class="rounded-3xl border border-neutral-3 bg-neutral-1 shadow-sm p-5 sm:p-7 flex flex-col gap-6 sm:gap-7 transition-all"
>
  <!-- 1. TIMELINE EN HAUT (CRITICAL REQUIREMENT) -->
  <etxe-property-application-timeline
    [status]="status()"
    [application]="application()"
    [appointment]="appointment()"
    [property]="property()"
  />

  <!-- 2. STATUS HEADER & QUICK ACTION BUTTONS -->
  <div class="pt-5 border-t border-neutral-3 flex flex-col lg:flex-row lg:items-start justify-between gap-5">
    <div class="flex items-start gap-4 min-w-0">
      <div
        class="flex size-13 sm:size-14 shrink-0 items-center justify-center rounded-2xl border shadow-xs"
        [class]="cfg.badgeBg"
      >
        <mat-icon class="size-7 sm:size-8 !text-2xl sm:!text-3xl flex items-center justify-center">
          {{ cfg.icon }}
        </mat-icon>
      </div>

      <div class="min-w-0">
        <div class="flex flex-wrap items-center gap-2 sm:gap-2.5 mb-1.5">
          <span
            class="px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider rounded-md border"
            [class]="cfg.badgeBg"
          >
            {{ cfg.badgeText }}
          </span>

          @if (finalOfferSubmittedAt(); as finalOfferDate) {
            <span class="text-xs font-medium text-neutral-11 flex items-center gap-1">
              <span class="text-neutral-8">·</span>
              <span>Offre finale transmise le {{ finalOfferDate | luxonDate: 'd MMMM yyyy' }}</span>
            </span>
          }

          @if (createdAt(); as createdDate) {
            <span class="text-xs text-neutral-10 flex items-center gap-1">
              <span class="text-neutral-8">·</span>
              <span>Candidature déposée le {{ createdDate | luxonDate: 'd MMMM yyyy' }}</span>
            </span>
          }
        </div>

        <h1 class="text-xl sm:text-2xl font-bold text-neutral-12 m-0 leading-tight">
          {{ cfg.title }}
        </h1>

        <p class="text-sm text-neutral-11 mt-1.5 mb-0 max-w-3xl leading-relaxed">
          {{ cfg.description }}
        </p>
      </div>
    </div>

    <div class="flex items-center gap-2 self-start shrink-0">
      <button
        mat-icon-button
        [matMenuTriggerFor]="actionMenu"
        class="!size-9 !text-neutral-10 hover:!text-neutral-12 hover:!bg-neutral-2 transition-all flex items-center justify-center rounded-lg"
        matTooltip="Options et actions"
        aria-label="Options de la candidature"
      >
        <mat-icon class="!text-xl size-5">more_vert</mat-icon>
      </button>

      <mat-menu #actionMenu="matMenu" xPosition="before">
        <button mat-menu-item (click)="viewProperty.emit()">
          <mat-icon class="!text-neutral-10">visibility</mat-icon>
          <span>Consulter la fiche du bien</span>
        </button>

        <button mat-menu-item (click)="openDiscussion.emit()">
          <mat-icon class="!text-neutral-10">forum</mat-icon>
          <span>Accéder à la messagerie</span>
        </button>

        @if (appointment()) {
          <button mat-menu-item (click)="openAgenda.emit()">
            <mat-icon class="!text-neutral-10">event</mat-icon>
            <span>Voir dans mon agenda</span>
          </button>
        }

        @if (canWithdraw()) {
          <mat-divider />
          <button mat-menu-item (click)="onWithdrawClick()" class="!text-red-11 hover:!bg-red-2">
            <mat-icon class="!text-red-9">cancel</mat-icon>
            <span class="font-medium text-red-11">Se retirer de la candidature</span>
          </button>
        }
      </mat-menu>
    </div>
  </div>

  <!-- 3. ESPACE DÉDIÉ POUR EFFECTUER L'ACTION -->
  <div class="flex flex-col gap-4">
    <!-- CASE A: PENDING ACTION ALERT -->
    @if (pendingAction(); as action) {
      @if (action.waitingFor === 'agent') {
        <div
          class="rounded-2xl border border-orange-4 bg-orange-2 p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-xs"
        >
          <div class="flex items-start gap-3.5">
            <div
              class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-orange-3 text-orange-11 border border-orange-5"
            >
              <mat-icon class="size-5 !text-xl">priority_high</mat-icon>
            </div>
            <div>
              <div class="text-xs font-bold uppercase tracking-wider text-orange-11">
                Action requise · {{ pendingActionLabel() }}
              </div>
              <div class="text-sm font-semibold text-neutral-12 mt-0.5">
                @if (action.actionType === 'final_offer') {
                  Vous pouvez maintenant formaliser votre avis de valeur, affiner vos honoraires et
                  transmettre votre offre finale au propriétaire.
                } @else {
                  Le propriétaire attend une action ou réponse de votre part.
                }
                @if (action.deadline) {
                  <span class="text-neutral-11 font-normal ml-1">
                    (Délai indicatif : avant le {{ action.deadline | luxonDate: 'd MMMM yyyy' }})
                  </span>
                }
              </div>
            </div>
          </div>

          @if (action.actionType === 'final_offer') {
            <button
              mat-flat-button
              class="primary shrink-0 !py-3.5 !px-5 font-bold text-xs flex items-center gap-2"
              (click)="adjustFinalOffer.emit()"
            >
              <mat-icon class="!text-lg size-5">edit_document</mat-icon>
              <span>Avis de valeur & offre finale</span>
            </button>
          } @else {
            <button
              mat-flat-button
              class="primary shrink-0 !py-3.5 !px-5 font-bold text-xs"
              (click)="openDiscussion.emit()"
            >
              Répondre dans la messagerie
            </button>
          }
        </div>
      } @else {
        <div
          class="rounded-2xl border border-indigo-4/70 bg-indigo-2/40 p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-xs"
        >
          <div class="flex items-start gap-3.5">
            <div
              class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-indigo-3 text-indigo-11 border border-indigo-4"
            >
              <mat-icon class="size-5 !text-xl">hourglass_top</mat-icon>
            </div>
            <div>
              <div class="text-xs font-bold uppercase tracking-wider text-indigo-11">
                En attente du propriétaire · {{ pendingActionLabel() }}
              </div>
              <div class="text-sm font-semibold text-neutral-12 mt-0.5">
                @if (action.actionType === 'mandate_selection') {
                  Votre avis de valeur et offre finale ont été transmis. Le propriétaire analyse les
                  propositions pour désigner l'agent mandataire.
                } @else if (action.actionType === 'visit_proposal') {
                  Votre candidature est en cours d'examen. Le propriétaire doit proposer des créneaux de
                  visite.
                } @else if (action.actionType === 'visit_reschedule') {
                  Une demande de reprogrammation a été formulée. Le propriétaire doit confirmer une nouvelle
                  date.
                } @else {
                  En attente du retour ou de la décision du propriétaire.
                }
                @if (action.deadline) {
                  <span class="text-neutral-11 font-normal ml-1">
                    (Délai de décision : avant le {{ action.deadline | luxonDate: 'd MMMM yyyy' }})
                  </span>
                }
              </div>
            </div>
          </div>

          <button
            mat-stroked-button
            class="shrink-0 !py-3.5 !px-5 font-semibold text-xs flex items-center gap-2 !border-neutral-4"
            (click)="openDiscussion.emit()"
          >
            <mat-icon class="!text-lg size-5">forum</mat-icon>
            <span>Accéder à la messagerie</span>
          </button>
        </div>
      }
    }

    <!-- CASE B: UPCOMING APPOINTMENT (HIDDEN IF ALREADY PASSED!) -->
    @if (isAppointmentUpcoming()) {
      @let appt = appointment();
      @let slot = confirmedSlot();
      <div
        class="rounded-2xl border border-indigo-4 bg-indigo-2/50 p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-xs"
      >
        <div class="flex items-start gap-3.5">
          <div
            class="flex size-11 shrink-0 items-center justify-center rounded-xl bg-indigo-3 text-indigo-11 border border-indigo-4"
          >
            <mat-icon class="size-6 !text-2xl">event</mat-icon>
          </div>
          <div>
            <div class="flex items-center gap-2">
              <span class="text-xs font-bold uppercase tracking-wider text-indigo-11"> Visite du bien </span>
              <span
                class="px-2 py-0.5 text-[11px] font-semibold rounded-md bg-indigo-3 text-indigo-11 border border-indigo-4"
              >
                {{ slot ? 'Confirmée' : 'En organisation' }}
              </span>
            </div>

            <div class="text-sm sm:text-base font-bold text-neutral-12 mt-1">
              @if (slot) {
                {{ slot.startAt | luxonDate: 'EEEE d MMMM yyyy à HH:mm' }}
              } @else {
                Créneaux proposés en attente de validation
              }
            </div>

            <div class="text-xs text-neutral-10 mt-0.5 flex items-center gap-1">
              <mat-icon class="!text-sm size-3.5 text-neutral-9">place</mat-icon>
              @if (appt?.location?.address; as addr) {
                <span>{{ addr }}</span>
              } @else if (property()?.location?.address?.city; as city) {
                <span>{{ city }}</span>
              }
            </div>
          </div>
        </div>

        <div class="flex items-center gap-2.5 shrink-0">
          <button
            mat-stroked-button
            class="!py-3.5 !px-4 font-semibold text-xs !border-indigo-4 hover:!bg-indigo-3/50"
            (click)="openAgenda.emit()"
          >
            Voir dans mon agenda
          </button>
          <button
            mat-button
            class="!text-indigo-11 hover:!bg-indigo-3/50 !py-3.5 !px-3 font-semibold text-xs"
            (click)="openDiscussion.emit()"
          >
            Gérer la visite
          </button>
        </div>
      </div>
    }

    <!-- CASE B-BIS: VISIT IS PASSED (AGENT PROACTIVE COMPLETION) -->
    @if (status() === 'visit_confirmed' && isVisitPassed()) {
      <div
        class="rounded-2xl border border-jade-5 bg-jade-2/70 p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-xs"
      >
        <div class="flex items-start gap-3.5">
          <div
            class="flex size-11 shrink-0 items-center justify-center rounded-xl bg-jade-3 text-jade-11 border border-jade-5"
          >
            <mat-icon class="size-6 !text-2xl">check_circle</mat-icon>
          </div>
          <div>
            <div class="flex items-center gap-2">
              <span class="text-xs font-bold uppercase tracking-wider text-jade-11"> Visite terminée ? </span>
              <span
                class="px-2 py-0.5 text-[11px] font-semibold rounded-md bg-jade-3 text-jade-11 border border-jade-5"
              >
                Créneau échu
              </span>
            </div>
            <div class="text-sm font-bold text-neutral-12 mt-1">
              Confirmez la réalisation de la visite sur site
            </div>
            <p class="text-xs text-neutral-11 m-0 mt-0.5">
              Cette action valide la visite et débloque le délai de 72h pour déposer votre avis de valeur et
              offre finale.
            </p>
          </div>
        </div>

        <div class="flex items-center gap-2.5 shrink-0 w-full sm:w-auto">
          <button
            mat-flat-button
            class="primary w-full sm:w-auto !py-3.5 !px-5 font-bold text-xs flex items-center justify-center gap-2 shadow-xs"
            (click)="completeVisit.emit()"
          >
            <mat-icon class="!text-lg size-5">task_alt</mat-icon>
            <span>Confirmer la visite effectuée</span>
          </button>
        </div>
      </div>
    }

    <!-- CASE C: CONTEXTUAL STAGE GUIDANCE CARD (WHEN NO DIRECT ALERT) -->
    @if (
      !pendingAction() && !isAppointmentUpcoming() && !(status() === 'visit_confirmed' && isVisitPassed())
    ) {
      <div
        class="rounded-2xl border border-neutral-3 bg-neutral-2/60 p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
      >
        <div class="flex items-start gap-3">
          <mat-icon class="size-5 !text-xl text-neutral-10 mt-0.5">lightbulb</mat-icon>
          <div class="text-xs sm:text-sm text-neutral-11">
            @switch (status()) {
              @case ('pending') {
                Le vendeur consulte les dossiers des professionnels. Vous pouvez lui envoyer un message de
                courtoisie pour lui témoigner votre réactivité.
              }
              @case ('visit_invited') {
                Le vendeur a ouvert les échanges pour planifier la visite. Choisissez un créneau qui convient
                à vos disponibilités.
              }
              @case ('visited') {
                Votre visite a eu lieu. Vous pouvez maintenant formaliser votre avis de valeur et déposer
                votre offre définitive.
              }
              @case ('offer_submitted') {
                Votre avis de valeur et offre finale ont été transmis. Le propriétaire examine les
                propositions finales avant de désigner l'agent retenu.
              }
              @case ('accepted') {
                Le mandat vous est accordé. Échangez avec le vendeur pour organiser la signature officielle et
                le lancement de la commercialisation.
              }
              @default {
                Consultez l'historique complet des échanges ou reprenez contact avec le vendeur via la
                messagerie.
              }
            }
          </div>
        </div>

        <button
          mat-stroked-button
          class="shrink-0 !py-3 !px-4 font-semibold text-xs !border-neutral-4 hover:!bg-neutral-3"
          (click)="openDiscussion.emit()"
        >
          Ouvrir la messagerie
        </button>
      </div>
    }
  </div>
</div>
